import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchBrand } from "@/lib/api";
import { useI18n, pickLocalized } from "@/lib/i18n";
import StatusChip from "@/components/StatusChip";
import Seo from "@/components/Seo";
import { ChevronDown, BadgeCheck, ExternalLink } from "lucide-react";
import { API } from "@/lib/api";
import ProfileAccordion from "@/components/brand/ProfileAccordion";
import BrandSidebar from "@/components/brand/BrandSidebar";
import NotifyWhenReadable from "@/components/brand/NotifyWhenReadable";

const CATS = ["PRODUCT_IDENTITY","MATERIALS","ORIGIN","REPAIR_CARE","END_OF_LIFE","FOOTPRINT","CERTIFICATIONS"];

function ChecklistRow({ item, catLabel, tNotFound, tEvidence, tViewSource, tCheckedOn, locale, testId }) {
  const [open, setOpen] = useState(false);
  const date = (item.checked_at || "").slice(0, 10);
  const localized = pickLocalized(item.evidence_excerpt, locale);
  // AI-03: if the localized slot is empty but we have an `original` slot
  // (source page was neither EN nor IT — e.g. Japanese or Chinese), surface
  // the excerpt with an honest language badge instead of pretending it's in
  // the viewer's language or hiding it entirely.
  const originalExcerpt = (item.evidence_excerpt && item.evidence_excerpt.original) || "";
  const sourceLanguage = item.source_language || "";
  const excerpt = localized || originalExcerpt;
  const showOriginalBadge = !localized && originalExcerpt && sourceLanguage && sourceLanguage !== locale;
  const showSourceChip = (item.status === "FOUND" || item.status === "PARTIAL") && item.source_url;
  let sourceHost = "";
  if (item.source_url) {
    try { sourceHost = new URL(item.source_url).hostname.replace(/^www\./, ""); } catch (e) { sourceHost = ""; }
  }
  return (
    <div className="border-t" style={{ borderColor: "var(--bp-border)" }} data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 gap-4 text-left"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg md:text-xl" style={{ color: "var(--bp-text)" }}>{catLabel}</div>
          {item.status === "NOT_FOUND" && (
            <div className="text-xs mt-1 italic" style={{ color: "var(--bp-text-muted)" }}>{tNotFound(date)}</div>
          )}
          {showSourceChip && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`${item.source_url} · ${tCheckedOn} ${date}`}
              className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-mono border transition-colors hover:bg-neutral-50"
              style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-secondary)" }}
              data-testid={`${testId}-inline-source`}
            >
              <ExternalLink size={11} aria-hidden />
              <span>Read the source</span>
              {sourceHost && <span className="opacity-60 hidden sm:inline"> · {sourceHost}</span>}
              <span className="opacity-60"> · {date}</span>
            </a>
          )}
        </div>
        <StatusChip status={item.status} testId={`${testId}-chip`} />
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: "var(--bp-text-muted)" }} />
      </button>
      {open && (
        <div className="pb-6 pl-1 text-sm" style={{ color: "var(--bp-text-secondary)" }}>
          {excerpt && (
            <div className="mb-3">
              <div className="uppercase text-xs tracking-widest mb-1 flex items-center gap-2" style={{ color: "var(--bp-text-muted)" }}>
                <span>{tEvidence}</span>
                {showOriginalBadge && (
                  <span
                    className="font-mono text-[10px] px-1.5 py-0.5 border"
                    style={{ borderColor: "var(--bp-border)", background: "#FFF7E0", color: "#8A5A2B" }}
                    data-testid={`${testId}-lang-badge`}
                    title={`Source page is in ${sourceLanguage.toUpperCase()} — no ${locale.toUpperCase()} translation was extracted`}
                  >
                    from source · {sourceLanguage.toUpperCase()}
                  </span>
                )}
              </div>
              <blockquote className="border-l-2 pl-3" style={{ borderColor: "var(--bp-border)" }}>{excerpt}</blockquote>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
            {item.source_url && <a className="bp-link" href={item.source_url} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-source`}>{tViewSource}</a>}
            <span>{tCheckedOn} <time>{date}</time></span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BrandProfile() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    fetchBrand(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bp-container py-24 text-center">
        <h1 className="font-serif text-4xl mb-4">Brand not indexed</h1>
        <p style={{ color: "var(--bp-text-secondary)" }}>This brand is not in the BrandPassport index yet.</p>
        <Link to="/not-indexed" className="bp-link mt-6 inline-block">Request its addition →</Link>
      </div>
    );
  }
  if (!data) return <div className="bp-container py-24"><div className="animate-pulse h-8 w-64 bg-neutral-200" /></div>;

  const { brand, profile, sector, changelog, sources, documents, data_trail, stale, access_restricted, crawl_policy } = data;
  const lastChecked = (profile?.last_reviewed_at || "").slice(0, 10);
  // Human-friendly freshness label — "Verificato il DD MMM YYYY" (IT) /
  // "Verified on DD MMM YYYY" (EN). Falls back to the ISO date when
  // Intl fails. Renders a small green tick badge when the profile
  // was reviewed within the last 90 days.
  const lastCheckedLabel = (() => {
    if (!lastChecked) return null;
    try {
      const d = new Date(lastChecked);
      if (Number.isNaN(d.getTime())) return lastChecked;
      return d.toLocaleDateString(locale === "it" ? "it-IT" : "en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return lastChecked; }
  })();
  const summary = pickLocalized(profile?.summary, locale);
  const dpp = pickLocalized(profile?.dpp_readiness_note, locale);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.display_name,
      legalName: brand.legal_name,
      url: brand.website_url,
      address: brand.hq_city ? { "@type": "PostalAddress", addressLocality: brand.hq_city, addressCountry: brand.country } : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (profile?.checklist || []).map((it) => ({
        "@type": "Question",
        name: `Does ${brand.display_name} publicly disclose ${t.checklist_categories[it.category]}?`,
        acceptedAnswer: { "@type": "Answer", text: `${t.status[it.status]} — checked ${(it.checked_at || "").slice(0,10)}.` },
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${brand.display_name} — transparency & Digital Product Passport readiness`}
        description={summary.slice(0, 160)}
        canonicalPath={`/brands/${brand.slug}`}
        locale={locale}
        ogImage={`${API}/brands/${brand.slug}/og.svg`}
        jsonLd={jsonLd}
      />

      {/* Brand header */}
      <section className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-10 md:py-14">
          <div className="flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>
            <Link to={`/sectors/${brand.sector_code.toLowerCase()}`} className="hover:underline">{brand.sector_code}</Link>
            <span>·</span>
            <span>{brand.country}</span>
            {brand.claimed_by_brand && (
              <span className="ml-2 px-2 py-0.5 border" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-secondary)" }} data-testid="claimed-badge">
                ✓ {t.claimed_badge}
              </span>
            )}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-none mb-4" data-testid="brand-title">{brand.display_name}</h1>
          {stale && (
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 border text-xs" style={{ borderColor: "var(--bp-border)", background: "#FFF8E1", color: "#B97700" }} data-testid="stale-note">
              <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: "#B97700" }} />
              Update in progress — this profile is due for a refresh
            </div>
          )}
          <p className="text-base md:text-lg max-w-3xl" style={{ color: "var(--bp-text-secondary)" }}>{pickLocalized(brand.short_description, locale)}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-mono" style={{ color: "var(--bp-text-secondary)" }}>
            <span data-testid="last-checked" className="inline-flex items-center gap-2">
              {lastCheckedLabel && !stale && (
                <span
                  aria-hidden
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 14, height: 14, borderRadius: 999,
                    background: "#DCFCE7", color: "#166534",
                    fontSize: 10, lineHeight: 1, fontWeight: 700,
                  }}
                  data-testid="freshness-badge"
                  title={locale === "it" ? "Verificato di recente" : "Recently verified"}
                >
                  ✓
                </span>
              )}
              {locale === "it" ? "Verificato il" : t.last_checked}: <time dateTime={lastChecked}>{lastCheckedLabel || "—"}</time>
            </span>
            {brand.website_url && <a className="bp-link" href={brand.website_url} target="_blank" rel="noopener noreferrer">{brand.website_url.replace(/https?:\/\//, "")}</a>}
            {!brand.claimed_by_brand && (
              <Link to={`/brands/${brand.slug}/claim`} className="bp-link inline-flex items-center gap-1" data-testid="claim-cta">
                <BadgeCheck size={14} /> Is this your brand?
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="bp-container py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {access_restricted && (
            <div className="mb-8 p-5 border-l-4" style={{ borderLeftColor: "#5A7A9A", background: "#F1F5F9" }} data-testid="access-restricted-banner">
              <div className="uppercase text-xs tracking-[0.3em] mb-2" style={{ color: "#2A3E56" }}>{t.access_restricted_banner_title}</div>
              <p className="text-sm md:text-base" style={{ color: "#2A3E56" }}>{t.access_restricted_banner_body}</p>
              <div className="mt-3 text-sm" style={{ color: "#2A3E56" }}>
                {t.notify_when_readable_intro}
              </div>
              <NotifyWhenReadable brandSlug={brand.slug} locale={locale} t={t} />
            </div>
          )}
          <div className="uppercase text-xs tracking-[0.3em] mb-4" style={{ color: "var(--bp-text-muted)" }}>Transparency checklist</div>
          <div className="border-b" style={{ borderColor: "var(--bp-border)" }}>
            {CATS.map((cat) => {
              const item = profile?.checklist?.find((c) => c.category === cat);
              if (!item) return null;
              return (
                <ChecklistRow
                  key={cat}
                  item={item}
                  catLabel={t.checklist_categories[cat]}
                  tNotFound={t.not_found_line}
                  tEvidence={t.evidence}
                  tViewSource={t.view_source}
                  tCheckedOn={t.checked_on}
                  locale={locale}
                  testId={`checklist-${cat}`}
                />
              );
            })}
          </div>

          <div className="mt-12">
            <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Summary</div>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--bp-text-secondary)" }} data-testid="brand-summary">{summary}</p>
          </div>

          <div className="mt-10">
            <ProfileAccordion
              brand={brand}
              profile={profile}
              sources={sources}
              documents={documents}
              changelog={changelog}
              dataTrail={data_trail}
              accessRestricted={access_restricted}
            />
          </div>
        </div>

        <BrandSidebar
          brand={brand}
          sector={sector}
          profile={profile}
          accessRestricted={access_restricted}
          crawlPolicy={crawl_policy}
          dpp={dpp}
          onCompareClick={() => nav(`/compare?brands=${brand.slug}`)}
        />
      </div>
    </>
  );
}
